
import React, { useMemo } from "react";
import {
    Box,
    IconButton,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { useTranslate } from "../Translate/TranslateContext";
import { useRegional } from "../Regional/RegionalContext";

export default function DashboardFieldGroupSelector({
    value,
    onChange,
    gardens,
    gardenGroups,
    supportsGroupSelection,
}) {
    const { translate } = useTranslate();
    const { fieldTerm } = useRegional();

    const hasGroups = supportsGroupSelection && gardenGroups.length > 0;
    const fieldPluralLabel = translate(`${fieldTerm}_plural`);

    const gardenNameById = useMemo(() => {
        return new Map(
            gardens.map((garden) => [
                Number(garden.garden_id),
                garden.garden_name,
            ])
        );
    }, [gardens]);

    const getGroupGardenNames = (group) => {
        return (group.garden_ids || [])
            .map((gardenId) => gardenNameById.get(Number(gardenId)))
            .filter(Boolean);
    };

    const renderGroupTooltip = (group) => {
        const gardenNames = getGroupGardenNames(group);

        return (
            <Box sx={{ minWidth: 160, maxWidth: 280 }}>
                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        mb: 0.75,
                    }}
                >
                    {fieldPluralLabel}
                </Typography>

                <Stack spacing={0.35}>
                    {gardenNames.length > 0 ? (
                        gardenNames.map((gardenName) => (
                            <Typography
                                key={`${group.group_id}-${gardenName}`}
                                sx={{
                                    fontSize: 12,
                                    lineHeight: 1.35,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {gardenName}
                            </Typography>
                        ))
                    ) : (
                        <Typography
                            sx={{
                                fontSize: 12,
                                lineHeight: 1.35,
                                opacity: 0.8,
                            }}
                        >
                            {translate(`${fieldTerm}_group_empty`)}
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    const headerSx = {
        backgroundColor: "var(--color-green-strong)",
        color: "var(--color-text-inverse)",
        fontWeight: 800,
        lineHeight: "34px",
        fontSize: 13,
        letterSpacing: "0.02em",
    };

    const itemSx = {
        backgroundColor: "var(--color-bg-primary)",
        color: "var(--color-text-primary)",
        fontSize: 14,

        "&:hover": {
            backgroundColor: "var(--color-green-soft)",
        },

        "&.Mui-selected": {
            backgroundColor: "var(--color-green-soft)",
            fontWeight: 700,
        },

        "&.Mui-selected:hover": {
            backgroundColor: "var(--color-green-soft)",
        },
    };

    return (
        <Select
            size="small"
            value={value}
            onChange={onChange}
            MenuProps={{
                MenuListProps: {
                    sx: {
                        py: 0,
                        backgroundColor: "var(--color-bg-primary)",
                    },
                },
                PaperProps: {
                    sx: {
                        mt: 1,
                        borderRadius: 2,
                        backgroundColor: "var(--color-bg-primary)",
                        border: "1px solid var(--color-green-strong)",
                        boxShadow: "0px 8px 24px rgba(0,0,0,0.16)",

                        maxHeight: "min(420px, calc(100dvh - 96px))",
                        overflowY: "auto",
                        overflowX: "hidden",

                        scrollbarWidth: "thin",
                        scrollbarColor:
                            "var(--color-green-strong) transparent",

                        "&::-webkit-scrollbar": {
                            width: 7,
                        },

                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "var(--color-green-strong)",
                            borderRadius: 8,
                        },

                        "&::-webkit-scrollbar-track": {
                            backgroundColor: "transparent",
                        },
                    },
                },
            }}
            sx={{
                minWidth: 260,
                ml: { xs: 0, md: "auto" },
                backgroundColor: "var(--color-green-strong)",
                color: "var(--color-text-inverse)",
                borderRadius: "999px",

                "& .MuiSelect-select": {
                    color: "var(--color-text-inverse)",
                    fontWeight: 700,
                },

                "& .MuiSvgIcon-root": {
                    color: "var(--color-text-inverse)",
                },

                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--color-green-strong)",
                },

                "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--color-green-strong)",
                },

                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--color-green-strong)",
                },
            }}
        >
            {hasGroups && (
                <ListSubheader disableSticky sx={headerSx}>
                    {translate("groups")}
                </ListSubheader>
            )}

            {hasGroups &&
                gardenGroups.map((group) => (
                    <MenuItem
                        key={`group-${group.group_id}`}
                        value={`group:${group.group_id}`}
                        sx={itemSx}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                gap: 1.5,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {group.group_name}
                            </Box>

                            <Tooltip
                                arrow
                                placement="right"
                                enterDelay={250}
                                title={renderGroupTooltip(group)}
                            >
                                <IconButton
                                    size="small"
                                    aria-label={fieldPluralLabel}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => event.stopPropagation()}
                                    sx={{
                                        flexShrink: 0,
                                        width: 24,
                                        height: 24,
                                        p: 0.25,
                                        color: "var(--color-green-strong)",

                                        "&:hover": {
                                            backgroundColor: "transparent",
                                            color: "var(--color-green-strong)",
                                        },

                                        "& .MuiSvgIcon-root": {
                                            color: "inherit",
                                            fontSize: 17,
                                        },
                                    }}
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </MenuItem>
                ))}

            <ListSubheader disableSticky sx={headerSx}>
                {fieldPluralLabel}
            </ListSubheader>

            {gardens.map((garden) => (
                <MenuItem
                    key={`garden-${garden.garden_id}`}
                    value={`garden:${garden.garden_id}`}
                    sx={itemSx}
                >
                    {garden.garden_name}
                </MenuItem>
            ))}
        </Select>
    );
}
